import React from 'react';
import { LedgerAccount } from 'general-ledger/types';
import { toastSuccess, toastError } from 'bench-internal-theme';
import useV2Mutation from 'utils/useV2Mutation';
import { UNARCHIVE_PERSONAL_TRANSACTIONS } from '../../mutation';
import moment from 'moment';
import { DatePicker } from 'inline-date-picker';
import { Modal } from 'modal-components';
import { openModal } from 'modal-core-components';
import Button from 'primitive-components/Button';
import {
  ConfirmationModalControls,
  ConfirmationModalHeader,
  ConfirmationModalLayout,
  PersonalLedgerContainer,
  PersonalLedgerCount,
  PersonalLedgerDescription,
  PersonalLedgerHeader,
  PersonalLedgerTableHeader,
  PersonalLedgerTableHeaderDescription,
  PersonalLedgerTableRow,
  PersonalLedgerTitle,
  PersonalTransactionsViewContentArea,
} from './PersonalTransactionsView.styles';

type PersonalTransactionsViewProps = {
  client: { id: number };
  ledgerAccounts: LedgerAccount[];
};

export const PersonalTransactionsView = ({
  client,
  ledgerAccounts,
}: PersonalTransactionsViewProps) => {
  const defaultEndDate = moment();
  const defaultStartDate = moment().startOf('year');
  const [unarchivePersonalTransactions, { loading }] = useV2Mutation(
    UNARCHIVE_PERSONAL_TRANSACTIONS,
    {
      onCompleted: ({
        updateClassificationStateForJournalEntries: { numberOfJournalEntriesUpdated },
      }) => {
        const successMessage =
          numberOfJournalEntriesUpdated === 0
            ? `No transactions found in selected date range for unarchiving.`
            : `Successfully unarchived ${numberOfJournalEntriesUpdated} transactions!`;
        toastSuccess(successMessage);
      },

      onError: () => {
        toastError(`There was a problem unarchiving transactions.`);
      },
    },
  );

  const handleSubmission =
    (clientId: number, ledgerAccountId: string) => (formValues: Record<string, string>) =>
      openModal(closeModal => (
        <Modal>
          <Modal.Header title="Confirm Submission" size="large" onClose={closeModal} />
          <Modal.Body>
            <ConfirmationModalLayout>
              <ConfirmationModalHeader>
                {`Are you sure you want to unarchive transactions between ${formValues.startDate} - ${formValues.endDate}?`}
              </ConfirmationModalHeader>
            </ConfirmationModalLayout>
          </Modal.Body>
          <Modal.Footer>
            <ConfirmationModalControls>
              <Button
                styleType="primary"
                type="submit"
                onClick={() => {
                  unarchivePersonalTransactions({
                    variables: {
                      input: {
                        ledgerAccountId,
                        clientId,
                        classificationState: 'PENDING',
                        filterBy: {
                          startDate: formValues.startDate,
                          endDate: formValues.endDate,
                          classificationValue: 'PERSONAL',
                        },
                      },
                    },
                  });

                  closeModal();
                }}
              >
                Confirm
              </Button>
              <Button styleType="danger" onClick={closeModal}>
                Cancel
              </Button>
            </ConfirmationModalControls>
          </Modal.Footer>
        </Modal>
      ));

  return (
    <PersonalTransactionsViewContentArea>
      <PersonalLedgerTitle>Personal Transactions</PersonalLedgerTitle>
      <PersonalLedgerHeader>
        Personal Accounts used for Business
        <PersonalLedgerCount>({ledgerAccounts.length})</PersonalLedgerCount>
      </PersonalLedgerHeader>
      <PersonalLedgerDescription>
        You can unlock a date range of archived personal transactions so that the customer can edit
        and re-submit them towards their business.
      </PersonalLedgerDescription>
      <PersonalLedgerContainer>
        <PersonalLedgerTableHeader>
          <PersonalLedgerTableHeaderDescription>LEDGER NAME</PersonalLedgerTableHeaderDescription>
          <PersonalLedgerTableHeaderDescription>
            EDITABLE TRANSACTION RANGE
          </PersonalLedgerTableHeaderDescription>
        </PersonalLedgerTableHeader>
        {ledgerAccounts.map(l => (
          <PersonalLedgerTableRow key={l.id}>
            <div>{l.name}</div>
            <DatePicker
              loading={loading}
              defaultEndDate={defaultEndDate}
              defaultStartDate={defaultStartDate}
              handleSubmission={handleSubmission(client.id, l.id)}
            />
          </PersonalLedgerTableRow>
        ))}
      </PersonalLedgerContainer>
    </PersonalTransactionsViewContentArea>
  );
};
